// Widget SaaS de Metamap (KYC, D18) — ÚNICA excepción a la regla puente D25: el front habla con un
// SDK de terceros que corre en el dispositivo del cliente, nunca con un lambda de Fiado.
//
// Molde: `fiado-backoffice-webapp/.../FMetaMapButton.tsx` + `VerificacionIdBlock.tsx`, que son las
// dos integraciones que ya funcionan en producción. Se conserva de ellas: el script del CDN cargado
// UNA sola vez con `id="metamap-script"` (reusándolo si ya está) y el elemento `<metamap-button>`
// creado por DOM con los atributos `clientid`, `flowId` y `metadata`.
//
// ⚠️ Diferencia deliberada con el molde: allá la metadata se ARMA en el front. Acá NO. El front no
// conoce `peopleId` ni `typeOfDirectoryId`; los tres valores llegan tal cual de
// `POST /wizard/sessions/{id}/kyc/prepare` y este componente no recalcula ninguno.
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Loading } from '@/kit';

const SCRIPT_ID = 'metamap-script';

export interface MetamapWidgetProps {
    /** Cuenta Metamap. Viene del `prepare` — el front no lo conoce ni lo configura. */
    clientId: string;
    /** Flow de Metamap por país. Viene del `prepare`. */
    flowId: string;
    /** `KycSdkMetadata` serializada en base64 por el wizard. Viene del `prepare`. */
    metadata: string;
    /** El cliente abrió la cámara del SDK. */
    onStarted?: () => void;
    /** El cliente terminó la captura (no implica aprobado: el resultado llega por webhook). */
    onFinished?: () => void;
    /** El cliente cerró el SDK sin terminar. */
    onExited?: () => void;
}

/** Carga el script del CDN una sola vez por documento; las montadas siguientes reusan el mismo. */
function loadMetamapScript(): Promise<void> {
    const src = import.meta.env.VITE_SRC_METAMAP;
    if (!src) return Promise.reject(new Error('Falta configurar VITE_SRC_METAMAP.'));

    return new Promise((resolve, reject) => {
        const existing = document.getElementById(SCRIPT_ID);
        if (existing) {
            // Ya cargado en una verificación anterior de esta misma pestaña.
            if (existing.dataset.loaded === 'true') resolve();
            else {
                existing.addEventListener('load', () => {
                    resolve();
                });
                existing.addEventListener('error', () => {
                    reject(new Error('No se pudo cargar el widget de verificación.'));
                });
            }
            return;
        }

        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = src;
        script.async = true;
        script.addEventListener('load', () => {
            script.dataset.loaded = 'true';
            resolve();
        });
        script.addEventListener('error', () => {
            reject(new Error('No se pudo cargar el widget de verificación.'));
        });
        document.body.appendChild(script);
    });
}

/**
 * El wizard serializa la metadata en base64, pero el atributo `metadata` del `<metamap-button>` se
 * lee como JSON — es lo que mandan las dos integraciones vivas de Fiado. Se DESENVUELVE el transporte
 * y se pasa el mismo objeto, sin tocar un solo campo: si la correlación viajara como un string
 * opaco, la verificación nacería huérfana y la sesión se colgaría en el paso 02 para siempre.
 *
 * Si el valor no fuera base64 (o el silo dejara de codificarlo) se pasa tal cual: el contrato manda,
 * no la suposición del front.
 */
export function toWidgetMetadata(metadata: string): string {
    try {
        const json = atob(metadata);
        JSON.parse(json);
        return json;
    } catch {
        return metadata;
    }
}

/** El `metamap-button` y los iframes que el SDK deja en la página. */
export const METAMAP_SDK_SELECTOR =
    'metamap-button, iframe[src*="metamap"], iframe[src*="getmati"]';

/** Quita el botón y el overlay del SDK, para que un SDK huérfano no siga emitiendo eventos. */
export function removeMetamapArtifacts(host: HTMLElement | null): void {
    host?.replaceChildren();
    document.querySelectorAll(METAMAP_SDK_SELECTOR).forEach((node) => {
        node.remove();
    });
}

export function MetamapWidget({
    clientId,
    flowId,
    metadata,
    onStarted,
    onFinished,
    onExited,
}: MetamapWidgetProps) {
    const hostRef = useRef<HTMLDivElement>(null);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Los callbacks se leen de un ref: la suscripción a `window` vive lo mismo que el widget.
    const callbacks = useRef({ onStarted, onFinished, onExited });
    useEffect(() => {
        callbacks.current = { onStarted, onFinished, onExited };
    });

    useEffect(() => {
        let cancelled = false;
        const host = hostRef.current;

        loadMetamapScript()
            .then(() => {
                if (cancelled || !host) return;
                host.replaceChildren();
                const button = document.createElement('metamap-button');
                button.setAttribute('clientid', clientId);
                button.setAttribute('flowId', flowId);
                button.setAttribute('metadata', toWidgetMetadata(metadata));
                host.appendChild(button);
                setReady(true);
            })
            .catch((e: unknown) => {
                if (!cancelled)
                    setError(e instanceof Error ? e.message : 'No se pudo abrir la verificación.');
            });

        return () => {
            cancelled = true;
            removeMetamapArtifacts(host);
        };
    }, [clientId, flowId, metadata]);

    useEffect(() => {
        const started = () => callbacks.current.onStarted?.();
        const finished = () => callbacks.current.onFinished?.();
        const exited = () => callbacks.current.onExited?.();
        window.addEventListener('metamap:userStartedSdk', started);
        window.addEventListener('metamap:userFinishedSdk', finished);
        window.addEventListener('metamap:exitedSdk', exited);
        return () => {
            window.removeEventListener('metamap:userStartedSdk', started);
            window.removeEventListener('metamap:userFinishedSdk', finished);
            window.removeEventListener('metamap:exitedSdk', exited);
        };
    }, []);

    if (error) {
        return (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-left">
                <AlertTriangle className="mt-0.5 size-4 flex-none text-destructive" aria-hidden />
                <div className="text-[12px] leading-relaxed text-destructive">
                    <p className="font-medium">No se pudo abrir la verificación</p>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center gap-3">
            {!ready && <Loading size="md" />}
            <div ref={hostRef} className="flex justify-center" />
        </div>
    );
}
